import { getRcToken, getRcUserId } from './rcCredentials';
import { getRcBaseUrl } from './rcBaseUrl';

type RCStatus = 'online' | 'away' | 'busy' | 'offline';

// RC enforces an 8-second minimum between users.setStatus calls per user.
// Use 10 seconds to give a small safety margin.
const MIN_GAP_MS = 10_000;

class RocketChatStatusService {
  private _lastStatus: RCStatus | null = null;
  private _lastMessage: string | null = null;
  private _lastSentAt = 0;

  // The latest desired state — overwritten on every call so only the most
  // recent status in any burst actually gets sent.
  private _pendingStatus: RCStatus | null = null;
  private _pendingMessage = '';
  private _timer: ReturnType<typeof setTimeout> | null = null;

  private get base(): string {
    return getRcBaseUrl();
  }

  private get headers(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'X-Auth-Token': getRcToken(),
      'X-User-Id': getRcUserId(),
    };
  }

  set(status: RCStatus, message = ''): void {
    if (!getRcToken() || !getRcUserId()) return;

    // Always track the latest desired state — last write in a burst wins.
    this._pendingStatus = status;
    this._pendingMessage = message;

    // Cancel any pending scheduled send and reschedule at the right time.
    if (this._timer !== null) clearTimeout(this._timer);

    // First-ever call goes out immediately; subsequent calls are deferred until
    // MIN_GAP_MS has elapsed since the last successful send.
    const msSinceLast = Date.now() - this._lastSentAt;
    const delay = this._lastSentAt === 0 ? 0 : Math.max(0, MIN_GAP_MS - msSinceLast);

    this._timer = setTimeout(() => {
      this._timer = null;
      const s = this._pendingStatus!;
      const m = this._pendingMessage;

      // Skip network call if status hasn't actually changed.
      if (s === this._lastStatus && m === this._lastMessage) return;

      this._lastStatus = s;
      this._lastMessage = m;
      this._lastSentAt = Date.now();

      fetch(`${this.base}/api/v1/users.setStatus`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({ status: s, message: m }),
      }).catch(() => {});
    }, delay);
  }

  online() {
    return this.set('online', '');
  }

  inMeeting() {
    return this.set('busy', 'In a meeting');
  }

  onBreak(reason = 'On a break') {
    return this.set('away', reason);
  }

  offline() {
    return this.set('offline', '');
  }
}

export const RocketChatStatusServiceInstance = new RocketChatStatusService();
